import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Space,
  DatePicker,
  Switch,
  Divider,
  Typography,
  Row,
  Col,
  Table,
  Modal,
  InputNumber,
  Tooltip,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
  createCampaign,
  updateCampaign,
  fetchCampaign,
  selectCurrentCampaign,
  selectPushLoading,
  selectSegments,
  fetchSegments,
} from 'store/slices/push.slice';
import {
  fetchApplications,
  selectApplications,
} from 'store/slices/applications.slice';
import {
  CampaignCreateRequest,
  PushType,
} from 'types/push.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import '../../styles/Common.css';
import PushTemplateLoader, { LoadedPush } from 'components/push/PushTemplateLoader';
import {getStoredAppId} from 'utils/pushCenterStorage'
import { asString, asNumber } from 'utils/resolve';

const { TextArea } = Input;
const { Title, Text: AntText } = Typography;
const { RangePicker } = DatePicker;

interface CampaignPushForm {
  key: string;
  sequence_order: number;
  delay_after_previous?: number;
  title: string;
  body: string;
  image_url?: string;
  custom_data?: Record<string, any>;
  segment_id?: number;
}

const calcSendTimes = (
  pushes: CampaignPushForm[],
  startDate?: dayjs.Dayjs
): dayjs.Dayjs[] => {
  if (!startDate) return [];
  const times: dayjs.Dayjs[] = [];
  let current = startDate;
  for (const push of pushes) {
    current = current.add(push.delay_after_previous ?? 0, 'minute');
    times.push(current);
  }
  return times;
};

const TIMEZONES: { gmt: string; tz: string; cities: string }[] = [
  { gmt: 'GMT−8', tz: 'America/Los_Angeles', cities: 'Лос-Анджелес, Сан-Франциско, Сиэтл' },
  { gmt: 'GMT−5', tz: 'America/New_York', cities: 'Нью-Йорк, Майами, Торонто' },
  { gmt: 'GMT−3', tz: 'America/Sao_Paulo', cities: 'Сан-Паулу, Буэнос-Айрес, Монтевидео' },
  { gmt: 'GMT+0', tz: 'Europe/London', cities: 'Лондон, Дублин, Лиссабон' },
  { gmt: 'GMT+1', tz: 'Europe/Berlin', cities: 'Берлин, Париж, Варшава' },
  { gmt: 'GMT+2', tz: 'Europe/Kiev', cities: 'Киев, Минск, Бухарест' },
  { gmt: 'GMT+3', tz: 'Europe/Moscow', cities: 'Москва, Стамбул, Эр-Рияд' },
  { gmt: 'GMT+5', tz: 'Asia/Tashkent', cities: 'Ташкент, Карачи, Екатеринбург' },
  { gmt: 'GMT+6', tz: 'Asia/Almaty', cities: 'Алматы, Дакка, Нур-Султан' },
  { gmt: 'GMT+8', tz: 'Asia/Shanghai', cities: 'Пекин, Сингапур, Куала-Лумпур' },
];

const formatInTimezone = (date: dayjs.Dayjs, tz: string): string => {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date.toDate());
};

const CampaignForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();

  const applications = useAppSelector(selectApplications) || [];
  const currentCampaign = useAppSelector(selectCurrentCampaign);
  const isLoading = useAppSelector(selectPushLoading);
  const segments = useAppSelector(selectSegments) || [];

  const [pushes, setPushes] = useState<CampaignPushForm[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPush, setEditingPush] = useState<CampaignPushForm | null>(null);
  const [pushForm] = Form.useForm();
  const [useSegment, setUseSegment] = useState(true);
  const dateRange = Form.useWatch('dateRange', form);
  const sendTimes = calcSendTimes(pushes, dateRange?.[0]);
  const campaignSegmentId = Form.useWatch('segment_id', form);

  const isEditMode = !!id;
  const selectedAppId = Form.useWatch('app_id', form);

  useEffect(() => {
      dispatch(fetchApplications({ limit: 100, offset: 0 }));

      if (!isEditMode) {
          const storedAppId = getStoredAppId();
          if (storedAppId) {
              form.setFieldValue('app_id', storedAppId);
          }
      }
  }, [dispatch]);

  useEffect(() => {
    if (selectedAppId) {
      dispatch(fetchSegments({ appId: selectedAppId, limit: 1000, offset: 0 }));
    }
  }, [selectedAppId, dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(fetchCampaign(Number(id)));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (currentCampaign && isEditMode && currentCampaign.app_id) {
      dispatch(fetchSegments({
        appId: currentCampaign.app_id,
        limit: 1000,
        offset: 0
      }));
    }

  }, [currentCampaign, isEditMode, dispatch]);

  useEffect(() => {
    if (currentCampaign && isEditMode) {
      const segmentId = asNumber(currentCampaign.segment_id);
      form.setFieldsValue({
        name: currentCampaign.name,
        description: currentCampaign.description,
        app_id: currentCampaign.app_id,
        push_type: currentCampaign.push_type,
        segment_id: segmentId,
        is_active: currentCampaign.is_active,
        dateRange: [
          dayjs(currentCampaign.start_date),
          currentCampaign.end_date ? dayjs(currentCampaign.end_date) : null,
        ],
      });

      if (currentCampaign.pushes) {
        setPushes(
          currentCampaign.pushes.map((push, index): CampaignPushForm => ({
            key: `push-${index}`,
            sequence_order: push.sequence_order,
            delay_after_previous: push.delay_after_previous,
            title: push.title,
            body: push.body,
            image_url: asString(push.image_url) ?? '',
            custom_data: push.custom_data,
            segment_id: asNumber(push.segment_id),
          }))
        );
      }
      const hasSegment = asNumber(currentCampaign.segment_id) !== undefined;
      const hasDirectFilters = currentCampaign.locale ||
        currentCampaign.has_registration !== undefined ||
        currentCampaign.has_deposit !== undefined ||
        currentCampaign.os ||
        currentCampaign.offer;
      setUseSegment(hasSegment || !hasDirectFilters);

    }
  }, [currentCampaign, isEditMode, form]);

  const handleAddPush = () => {
    setEditingPush(null);
    pushForm.resetFields();
    setModalVisible(true);
  };

  const handleLoadFromTemplate = (loadedPushes: LoadedPush[], templateName?: string, targetLang?: string) => {
    if (templateName) {
      const nameWithLang = targetLang
        ? `${templateName} - ${targetLang.toUpperCase()}`
        : templateName;

      const currentName = form.getFieldValue('name');
      if (!currentName) {
        form.setFieldsValue({
          name: nameWithLang,
          description: nameWithLang,
        });
      } else if (!form.getFieldValue('description')) {
        form.setFieldValue('description', currentName);
      }
    }

    const newPushes: CampaignPushForm[] = loadedPushes.map((p, i) => ({
      key: `push-template-${Date.now()}-${i}`,
      sequence_order: pushes.length + i + 1,
      title: p.title,
      body: p.body,
      image_url: p.image_url ?? '',
      delay_after_previous: p.delay_after_previous,
    }));
    setPushes((prev) => [...prev, ...newPushes]);
  };

  const handleEditPush = (push: CampaignPushForm) => {
    setEditingPush(push);
    pushForm.setFieldsValue({
      ...push,
      segment_id: push.segment_id,
      custom_data: push.custom_data ? JSON.stringify(push.custom_data, null, 2) : '',
    });
    setModalVisible(true);
  };

  const handleDeletePush = (key: string) => {
    setPushes((prev) => prev.filter((p) => p.key !== key));
  };
  
  const handleMovePush = (index: number, direction: 'up' | 'down') => {
    const newPushes = [...pushes];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newPushes.length) return;

    [newPushes[index], newPushes[targetIndex]] = [newPushes[targetIndex], newPushes[index]];

    newPushes.forEach((push, idx) => {
      push.sequence_order = idx + 1;
    });

    setPushes(newPushes);
  };

  const handlePushModalOk = async () => {
    try {
      const values = await pushForm.validateFields();

      let customData = undefined;
      if (values.custom_data) {
        try {
          customData = JSON.parse(values.custom_data);
        } catch {
          notify.error('Неверный формат JSON в Custom Data');
          return;
        }
      }

      const pushData: CampaignPushForm = {
        key: editingPush?.key || `push-${Date.now()}`,
        sequence_order: editingPush?.sequence_order || pushes.length + 1,
        title: values.title,
        body: values.body,
        image_url: asString(values.image_url) ?? '',
        delay_after_previous: values.delay_after_previous,
        segment_id: values.segment_id,
        custom_data: customData,
      };

      if (editingPush) {
        setPushes((prev) =>
          prev.map((p) => (p.key === editingPush.key ? pushData : p))
        );
      } else {
        setPushes((prev) => [...prev, pushData]);
      }

      setModalVisible(false);
      pushForm.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    if (pushes.length === 0) {
      notify.error('Добавьте хотя бы одно push-уведомление в кампанию');
      return;
    }

    try {
      const [startDate, endDate] = values.dateRange || [];

      const payload: CampaignCreateRequest = {
        app_id: values.app_id,
        name: values.name,
        description: values.description,
        push_type: values.push_type,
        segment_id: asNumber(values.segment_id),
        start_date: startDate.toISOString(),
        end_date: endDate ? endDate.toISOString() : undefined,
        is_active: values.is_active ?? true,
        pushes: pushes.map((push) => ({
          sequence_order: push.sequence_order,
          delay_after_previous: push.delay_after_previous,
          title: push.title,
          body: push.body,
          image_url: push.image_url || undefined,
          custom_data: push.custom_data,
          segment_id: push.segment_id,
        })),
      };

      console.log('=== PAYLOAD ===', JSON.stringify(payload, null, 2));

      if (isEditMode) {
        await dispatch(
          updateCampaign({ id: Number(id), data: payload })
        ).unwrap();
        notify.success('Кампания успешно обновлена');
      } else {
        await dispatch(createCampaign(payload)).unwrap();
        notify.success('Кампания успешно создана');
      }

      navigate(ROUTES.PUSH_CENTER);
    } catch (error: any) {
      notify.error(error.message || 'Ошибка при сохранении кампании');
    }
  };

  const pushColumns: ColumnsType<CampaignPushForm> = [
    {
      title: '№',
      dataIndex: 'sequence_order',
      key: 'sequence_order',
      width: 60,
      align: 'center',
    },
    {
      title: 'Заголовок',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{title}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            {record.body}
          </div>
        </div>
      ),
    },
    {
      title: 'Сегмент',
      dataIndex: 'segment_id',
      key: 'segment_id',
      width: 130,
      render: (segmentId?: number) => {
        const effectiveId = segmentId ?? campaignSegmentId;
        if (!effectiveId) {
          return <Tag color="default">Все пользователи</Tag>;
        }
        const segment = segments?.find((s) => s?.id === effectiveId);
        const isInherited = !segmentId && !!campaignSegmentId;
        return (
          <Tooltip title={isInherited ? 'Унаследован из кампании' : undefined}>
            <Tag color={isInherited ? 'default' : 'blue'}>
              {segment ? segment.name : `ID: ${effectiveId}`}
              {isInherited && ' (кампания)'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Задержка (мин)',
      dataIndex: 'delay_after_previous',
      key: 'delay_after_previous',
      width: 120,
      align: 'center',
      render: (delay?: number) => (delay ? `${delay} мин` : 'Сразу'),
    },
    {
      title: 'Время отправки',
      key: 'send_time',
      width: 150,
      align: 'center',
      render: (_, __, index) => {
        const t = sendTimes[index];
        return t ? (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500 }}>{t.format('DD.MM.YYYY')}</div>
            <div style={{ color: '#1677ff' }}>{t.format('HH:mm')}</div>
          </div>
        ) : (
          <AntText type="secondary" style={{ fontSize: 12 }}>нет даты</AntText>
        );
      },
    },
    {
      title: <ClockCircleOutlined />,
      key: 'timezones',
      width: 40,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => {
        const t = sendTimes[index];
        if (!t) return null;

        const content = (
          <div style={{ minWidth: 440 }}>
            {TIMEZONES.map(({ gmt, tz, cities }) => (
              <div
                key={tz}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                  padding: '4px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div style={{ flexShrink: 0 }}>
                  <span style={{ fontWeight: 600, color: '#1677ff', fontSize: 9 }}>{gmt}</span>
                  <span style={{ color: '#aaa', fontSize: 9, marginLeft: 3 }}>
                    ({cities})
                  </span>
                </div>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: 9 }}>
                  {formatInTimezone(t, tz)}
                </span>
              </div>
            ))}
          </div>
        );

        return (
          <Tooltip
            title={content}
            trigger="hover"
            overlayStyle={{ maxWidth: 480 }}
          >
            <ClockCircleOutlined style={{ color: '#1677ff', cursor: 'pointer', fontSize: 15 }} />
          </Tooltip>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 150,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => handleMovePush(index, 'up')}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === pushes.length - 1}
            onClick={() => handleMovePush(index, 'down')}
          />
          <Button
            type="link"
            size="small"
            onClick={() => handleEditPush(record)}
          >
            Изменить
          </Button>
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeletePush(record.key)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <Title level={2}>
            {isEditMode ? 'Редактирование кампании' : 'Создание кампании'}
          </Title>
          <AntText type="secondary">
            Кампания позволяет отправлять последовательность push-уведомлений
          </AntText>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            push_type: PushType.REGULAR,
            is_active: true,
          }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <Card title="Основная информация" style={{ marginBottom: 16 }}>
                <Form.Item
                  name="name"
                  label="Название кампании"
                  rules={[
                    { required: true, message: 'Введите название кампании' },
                    { min: 3, message: 'Минимум 3 символа' },
                  ]}
                >
                  <Input placeholder="Например: Приветственная серия" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Описание"
                  rules={[{ required: true, message: 'Введите описание' }]}
                >
                  <TextArea
                    placeholder="Краткое описание кампании"
                    rows={3}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="app_id"
                      label="Приложение"
                      rules={[{ required: true, message: 'Выберите приложение' }]}
                    >
                      <Select
                        placeholder="Выберите приложение"
                        disabled={isEditMode}
                      >
                        {applications.map((app) => (
                          <Select.Option key={app.id} value={app.id}>
                            {app.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="push_type"
                      label="Тип уведомлений"
                      rules={[{ required: true }]}
                    >
                      <Select>
                        <Select.Option value={PushType.REGULAR}>
                          Обычные
                        </Select.Option>
                        <Select.Option value={PushType.EVENT}>
                          Событийные
                        </Select.Option>
                        <Select.Option value={PushType.SPECIAL}>
                          Специальные
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="segment_id"
                  label="Сегмент кампании (по умолчанию)"
                  tooltip="Этот сегмент будет применен ко всем push-уведомлениям, у которых не указан собственный сегмент."
                >
                  <Select
                    placeholder="Все пользователи (по умолч.)"
                    allowClear
                    loading={isLoading}
                    disabled={!selectedAppId}
                  >
                    {segments?.map((seg) => (
                      <Select.Option key={seg.id} value={seg.id}>
                        {seg.name} ({seg.user_count} пользователей)
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="dateRange"
                  label="Период действия"
                  rules={[{ required: true, message: 'Выберите период' }]}
                >
                  <RangePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    style={{ width: '100%' }}
                    placeholder={['Дата начала', 'Дата окончания']}
                  />
                </Form.Item>
              </Card>
              <Card
                title="Push-уведомления в кампании"
                extra={
                  <Space>
                    <PushTemplateLoader
                      disabled={!selectedAppId}
                      onLoad={handleLoadFromTemplate}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPush}>
                      Добавить Push
                    </Button>
                  </Space>
                }
              >
                {pushes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                    <AntText>Кампания пока не содержит push-уведомлений</AntText>
                    <div style={{ marginTop: 16 }}>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddPush}
                      >
                        Добавить первое уведомление
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Table
                    columns={pushColumns}
                    dataSource={pushes}
                    pagination={false}
                    size="small"
                    rowKey="key"
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card title="Параметры">
                <Form.Item
                  name="is_active"
                  label="Статус"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Активна"
                    unCheckedChildren="Неактивна"
                  />
                </Form.Item>

                <Divider />

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={isLoading}
                    size="large"
                    block
                  >
                    {isEditMode ? 'Сохранить изменения' : 'Создать кампанию'}
                  </Button>
                  <Button
                    onClick={() => navigate(ROUTES.PUSH_CENTER)}
                    block
                  >
                    Отмена
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Card>
      <Modal
        title={editingPush ? 'Редактирование Push' : 'Добавление Push'}
        open={modalVisible}
        onOk={handlePushModalOk}
        onCancel={() => setModalVisible(false)}
        width={700}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={pushForm} layout="vertical">
          <Form.Item
            name="title"
            label="Заголовок"
            rules={[
              { required: true, message: 'Введите заголовок' },
              { max: 100, message: 'Максимум 100 символов' },
            ]}
          >
            <Input placeholder="Заголовок уведомления" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="body"
            label="Текст сообщения"
            rules={[
              { required: true, message: 'Введите текст' },
              { max: 500, message: 'Максимум 500 символов' },
            ]}
          >
            <TextArea
              placeholder="Текст уведомления"
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item name="image_url" label="URL изображения">
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Form.Item
            name="delay_after_previous"
            label="Задержка после предыдущего (минуты)"
            tooltip="Через сколько минут отправить это уведомление после предыдущего. 0 = сразу."
          >
            <InputNumber
              min={0}
              max={10080}
              placeholder="0"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="segment_id"
            label="Сегмент Push-уведомления"
            tooltip="Если не указан, будет использован сегмент кампании."
          >
            <Select
              placeholder="Использовать сегмент кампании (по умолч.)"
              allowClear
              loading={isLoading}
              disabled={!selectedAppId}
            >
              {segments?.map((seg) => (
                <Select.Option key={seg.id} value={seg.id}>
                  {seg.name} ({seg.user_count} пользователей)
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Тип таргетинга">
            <Space>
              <Switch
                checked={useSegment}
                onChange={(checked) => {
                  setUseSegment(checked);
                  if (checked) {
                    form.setFieldsValue({
                      locale: undefined,
                      has_registration: undefined,
                      has_deposit: undefined,
                      os: undefined,
                      offer: undefined,
                    });
                  } else {
                    form.setFieldsValue({ segment_id: undefined });
                  }
                }}
              />
              <AntText>{useSegment ? 'Использовать сегмент' : 'Прямые фильтры'}</AntText>
            </Space>
          </Form.Item>

          {useSegment ? (
            <Form.Item
              name="segment_id"
              label="Сегмент кампании"
              tooltip="Этот сегмент будет применён ко всем push-уведомлениям кампании"
            >
              <Select
                placeholder="Все пользователи"
                allowClear
                loading={isLoading}
                disabled={!selectedAppId}
              >
                {segments?.map((seg) => (
                  <Select.Option key={seg.id} value={seg.id}>
                    {seg.name} ({seg.user_count} пользователей)
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Card size="small" title="Фильтры аудитории" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="locale" label="Локаль">
                    <Select placeholder="Любая" allowClear>
                      {['ru', 'en', 'uk', 'kz', 'uz', 'az', 'tr', 'pt', 'es', 'de'].map(loc => (
                        <Select.Option key={loc} value={loc}>
                          {loc.toUpperCase()}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="os" label="Платформа">
                    <Select placeholder="Любая" allowClear>
                      <Select.Option value="android">Android</Select.Option>
                      <Select.Option value="ios">iOS</Select.Option>
                      <Select.Option value="web">Web</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="offer" label="Оффер">
                    <Input placeholder="Любой" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="has_registration" label="Есть регистрация">
                    <Select placeholder="Неважно" allowClear>
                      <Select.Option value={true}>Да</Select.Option>
                      <Select.Option value={false}>Нет</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="has_deposit" label="Есть депозит">
                    <Select placeholder="Неважно" allowClear>
                      <Select.Option value={true}>Да</Select.Option>
                      <Select.Option value={false}>Нет</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          <Form.Item
            name="custom_data"
            label="Дополнительные данные (JSON)"
          >
            <TextArea
              placeholder='{"key": "value"}'
              rows={3}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CampaignForm;